/**
 * Runtime API data-path switch — the kill-switch used when the Yandex API Gateway is unavailable.
 *
 * Two positions, matching the variations of the `api-data-path` flag:
 *   gateway  — REST through the Yandex API Gateway (api[-dev].arendavmeste.ru). The default.
 *   firebase — no REST at all: every service takes its Firestore SDK / callable branch, i.e. it
 *              talks to Firebase directly. This is the path the product used before the gateway
 *              migration and it still covers every flow, payments included
 *              (PaymentService → httpsCallable('initiatePayment')).
 *
 * `firebase` does NOT mean "REST against Firebase Hosting". That was considered and rejected:
 * Hosting rewrites all of /v1/** to the restApi function, which does not serve /v1/payments/*
 * (those live in the separate restPayments function that the gateway routes to), so payments
 * would 404 — as would the 33 operations implemented only in yandex-functions/.
 *
 * The mode is resolved ONCE per page load, so a session can never flip data path mid-flight
 * (same rule the gate's dataPath override follows). Priority:
 *   1. ?apiMode=<mode>       — try a mode in one browser; also pins it for later loads on this
 *                              browser (?apiMode=auto unpins and returns to the flag value)
 *   2. localStorage override — what step 1 pinned
 *   3. the flag value        — whatever the host app passes to applyApiMode() at boot (the
 *                              `api-data-path` LaunchDarkly flag; see config/apiModeBoot.ts)
 *   4. localStorage cache    — the last flag value this browser saw, used when the flag cannot
 *                              be read in time (offline, blocked network, LD unreachable)
 *   5. DEFAULT_API_MODE
 *
 * This module deliberately knows nothing about the flag backend: shared-lib stays below the host
 * app, which owns the SDK. Rolling back is a flag change, not a deploy — set `api-data-path` back
 * to "gateway" and every browser follows on its next load.
 */
/** What the app does with no override, no remote value and no cache. */
export const DEFAULT_API_MODE = 'gateway';
const API_MODES = ['gateway', 'firebase'];
const QUERY_PARAM = 'apiMode';
const UNPIN_VALUE = 'auto';
const OVERRIDE_STORAGE_KEY = 'apiMode:override';
const CACHE_STORAGE_KEY = 'apiMode:cache';
/** `gateway` mode — Yandex API Gateway per SPA hostname. */
const GATEWAY_API_URLS = {
    'y-dev.arendavmeste.ru': 'https://api-dev.arendavmeste.ru',
    'y.arendavmeste.ru': 'https://api.arendavmeste.ru',
    'arendavmeste.ru': 'https://api.arendavmeste.ru',
    'office-rent-dev.web.app': 'https://api-dev.arendavmeste.ru',
    // Local dev hits the dev API to match prod-like (REST) behaviour.
    localhost: 'https://api-dev.arendavmeste.ru',
};
let activeMode;
function isApiMode(value) {
    return typeof value === 'string' && API_MODES.includes(value);
}
function currentHostname() {
    return typeof window === 'undefined' ? '' : window.location.hostname || '';
}
function readStorage(key) {
    try {
        return window.localStorage.getItem(key);
    }
    catch (_a) {
        return null;
    }
}
function writeStorage(key, value) {
    try {
        window.localStorage.setItem(key, value);
    }
    catch (_a) {
        // Private mode / blocked site data — the switch still works for this page load.
    }
}
function removeStorage(key) {
    try {
        window.localStorage.removeItem(key);
    }
    catch (_a) {
        // Same as writeStorage: nothing to do if site data is unavailable.
    }
}
/** `?apiMode=` — a valid mode pins it on this browser, `auto` unpins. */
function readQueryOverride() {
    if (typeof window === 'undefined')
        return null;
    let raw = null;
    try {
        raw = new URL(window.location.href).searchParams.get(QUERY_PARAM);
    }
    catch (_a) {
        return null;
    }
    if (raw === UNPIN_VALUE) {
        removeStorage(OVERRIDE_STORAGE_KEY);
        return null;
    }
    if (!isApiMode(raw))
        return null;
    writeStorage(OVERRIDE_STORAGE_KEY, raw);
    return raw;
}
function readOverride() {
    const fromQuery = readQueryOverride();
    if (fromQuery)
        return fromQuery;
    const stored = readStorage(OVERRIDE_STORAGE_KEY);
    return isApiMode(stored) ? stored : null;
}
function readCache() {
    const stored = readStorage(CACHE_STORAGE_KEY);
    return isApiMode(stored) ? stored : null;
}
/**
 * The active mode. Resolved on first use and then fixed for this page load, so every call site
 * sees the same data path.
 */
export function getApiMode() {
    if (activeMode)
        return activeMode;
    activeMode = readOverride() || readCache() || DEFAULT_API_MODE;
    return activeMode;
}
/**
 * Hands the flag value to this module at boot, before the first render. Anything that is not one
 * of the three modes (missing flag, unreachable backend, typo in a variation) is ignored, and the
 * mode falls back to this browser's cached value and then to the default. A `?apiMode=` pin always
 * wins, so a browser verifying one mode is not pulled back by the flag.
 */
export function applyApiMode(value) {
    if (isApiMode(value)) {
        writeStorage(CACHE_STORAGE_KEY, value);
    }
    activeMode =
        readOverride() ||
            (isApiMode(value) ? value : null) ||
            readCache() ||
            DEFAULT_API_MODE;
    return activeMode;
}
/**
 * Base URL for REST calls in the active mode. An empty string means "no REST" — the services
 * then take their direct Firestore/callable branch.
 */
export function getApiBaseUrl() {
    if (getApiMode() === 'firebase')
        return '';
    return GATEWAY_API_URLS[currentHostname()] || '';
}
/** Clears the once-per-page-load memo. Tests only. */
export function resetApiMode() {
    activeMode = undefined;
}
